<template>
	<v-card>
		<v-card-title
			class="d-flex align-center mb-4"
		>
			<v-icon
				icon="$user"
				start
			/>
			<translate>Customer Data</translate>
		</v-card-title>
		<v-card-text>
			<v-row>
				<v-col>
					<v-row>
						<v-col>
							<v-form
								ref="email"
								@keydown.enter.prevent
							>
								<v-text-field
									v-model="client.email"
									:rules="[v => emailRegex.test(v) || $gettext('Enter an email')]"
									:label="$gettext('Find User')"
									variant="outlined"
									@keydown.enter.prevent="findUser"
								/>
							</v-form>
						</v-col>
						<v-col
							cols="auto"
							class="d-flex align-center mb-6"
						>
							<v-btn
								color="primary"
								variant="flat"
								@click="findUser"
							>
								<translate>Search Users</translate>
							</v-btn>
						</v-col>
					</v-row>
					<div v-if="knownUser !== null && (step === 1)">
						<v-row>
							<v-col>
								<div v-if="knownUser">
									<h4><translate>TulipAssist has a customer with the given email</translate></h4>
									<div v-if="!hasActiveInsurance">
										<translate>The customer currently has no active insurance</translate>
									</div>
									<div v-if="!userProductDomainMatches">
										<translate>The country of the user does not match the product domain</translate>
									</div>
								</div>
								<h4 v-else-if="knownUser === false">
									<translate>TulipAssist has no customer with the given email</translate>
								</h4>
							</v-col>
						</v-row>
						<v-row
							v-if="knownUser !== null"
							class="d-flex align-center"
						>
							<v-col
								v-if="knownUser"
								cols="auto"
							>
								<v-btn
									color="primary"
									variant="flat"
									:disabled="!userProductDomainMatches"
									@click="step = 2"
								>
									<translate>This Customer</translate>
								</v-btn>
							</v-col>
							<v-col
								v-if="knownUser"
								cols="auto"
							>
								<v-btn
									color="primary"
									variant="flat"
									@click="resetUserForm"
								>
									<translate>A New Customer</translate>
								</v-btn>
							</v-col>
							<v-col
								v-if="knownUser === false"
								cols="auto"
							>
								<v-btn
									color="primary"
									variant="flat"
									@click="resetUserData && (step = 2)"
								>
									<translate>
										A New Customer
									</translate>
								</v-btn>
							</v-col>
						</v-row>
					</div>
				</v-col>
			</v-row>
			<v-row v-if="(knownUser === true) || (step === 2)">
				<v-col>
					<v-divider />
					<v-form
						ref="clientForm"
					>
						<v-row>
							<v-col>
								<translate class="text-primary">
									Name
								</translate>
								<v-divider class="bg-primary" />
								<v-text-field
									v-model="client.name.initials"
									:rules="[v => !!v || !!client.name.first || $gettext('Initials or first name is required')]"
									:label="$gettext('Initials')"
									required
									:disabled="knownUser"
									variant="outlined"
								/>

								<v-text-field
									v-model="client.name.first"
									:rules="[v => !!v || !!client.name.initials || $gettext('Initials or first name is required')]"
									:label="$gettext('First Name')"
									required
									:disabled="knownUser"
									variant="outlined"
								/>
								<v-text-field
									v-model="client.name.infix"
									:label="$gettext('infix')"
									:disabled="knownUser"
									variant="outlined"
								/>
								<v-text-field
									v-model="client.name.last"
									:rules="[v => !!v || $gettext('Last Name is required')]"
									:label="$gettext('Last Name')"
									required
									:disabled="knownUser"
									variant="outlined"
								/>
								<translate class="text-primary">
									Address
								</translate>
								<v-divider class="bg-primary" />
								<address-fields
									v-model="client.address"
									:country-name="client.address.country"
									:outlined="false"
									:disabled="hasActiveInsurance"
								/>
							</v-col>
							<v-col>
								<translate class="text-primary">
									Details
								</translate>
								<v-divider class="bg-primary" />
								<v-row>
									<v-col>
										<date-text-field
											v-model:model-value="client.dateBirth"
											required
											:disabled="knownUser"
										/>
										<v-select
											v-model="client.gender"
											:rules="[v => !!v || $gettext('Gender is required')]"
											:items="genders"
											:item-title="item => $gettext(item.name)"
											item-value="value"
											:label="$gettext('Gender')"
											required
											:disabled="knownUser"
											variant="outlined"
										/>
										<v-text-field
											v-model="client.telephonenumber"
											:rules="[v => !!v || $gettext('Telephone Number is required'), v => /^\+(?:[0-9] ?){6,14}[0-9]$/.test(v) || $gettext('Telephone number must be international format')]"
											:label="$gettext('Telephone Number')"
											required
											:disabled="hasActiveInsurance"
											variant="outlined"
										/>
										<v-select
											v-model="client.language"
											:rules="[v => !!v || $gettext('Language is required')]"
											:items="languages"
											item-title="name"
											item-value="code"
											:label="$gettext('Language')"
											required
											:disabled="hasActiveInsurance"
											variant="outlined"
										/>
										<v-text-field
											v-model="client.bankaccount.iban"
											:rules="[v => !!v || $gettext('IBAN is required')]"
											:label="$gettext('IBAN')"
											required
											:disabled="hasActiveInsurance"
											variant="outlined"
										/>
										<v-switch
											v-model="client.isBusiness"
											class="ma-4"
											color="primary"
											inset
											:label="$gettext('Is business')"
											:disabled="hasActiveInsurance"
											variant="outlined"
										/>
										<div v-if="client.isBusiness">
											<translate>Business Details</translate>
											<v-divider />
											<v-text-field
												v-model="client.business.name"
												:rules="[v => !!v || $gettext('Business Name is required')]"
												:label="$gettext('Business Name')"
												:disabled="hasActiveInsurance"
												variant="outlined"
											/>
											<v-text-field
												v-model="client.business.kvk"
												:rules="[v => !!v || $gettext('Chamber of Commerce number is required')]"
												:label="$gettext('Chamber of Commerce Number')"
												:disabled="hasActiveInsurance"
												variant="outlined"
											/>
										</div>
										<v-file-input
											v-model="clientDocument"
											show-size
											:label="$gettext('Client ID Document')"
											:disabled="knownUser"
											variant="outlined"
										/>
									</v-col>
								</v-row>
							</v-col>
						</v-row>
					</v-form>
				</v-col>
			</v-row>
		</v-card-text>
		<v-card-actions>
			<v-btn
				variant="plain"
				@click="$emit('update:previous')"
			>
				<translate>Back</translate>
			</v-btn>
			<v-spacer />
			<v-btn
				v-if="step === 2"
				color="primary"
				@click="validateUser"
			>
				<translate>Create insurance</translate>
			</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
import axios from 'axios';
import {
	displayError,
} from 'laravel-front-library';

export default {
	name: 'OrderCustomerSelect',
	props: {
		domain: {
			type: Object,
			required: true,
		}
	},

	emits: ['update:client', 'update:previous', 'update:document',],

	data () {
		return {
			step: 1,
			hasActiveInsurance: false,
			knownUser: null,
			languages: [],
			clientDocument: [],
			emailRegex: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
			client: {
				email: '',
				name: {
					initials: null,
					infix: null,
					first: null,
					last: null
				},
				gender: null,
				dateBirth: null,
				telephonenumber: null,
				language: null,
				address: {
					street: null,
					number: null,
					numberSuffix: null,
					city: null,
					country: this.domain.country.name,
					postalcode: null,
					administrativeArea: null,
				},
				bankaccount: {
					iban: null
				},
				isBusiness: false,
				business: {
					name: null,
					kvk: null,
				},
				riskLevel: false,
			},
			genders: [{
				value: 'M', name: 'Male'
			}, {
				value: 'F', name: 'Female'
			}, ],
		};
	},

	computed: {
		/**
		 * User domain and product domain match check
		 *
		 * @returns {boolean} If the domains match
		 */
		userProductDomainMatches: function () {
			return this.client.address.country === this.domain.country.name;
		},
	},


	/**
	 * Get all the necessary data from the api.
	 *
	 * @returns {void}
	 */
	created () {
		this.getLanguages();
	},

	methods: {

		/**
		 * Get languages we support
		 *
		 * @returns {void}
		 */
		getLanguages () {
			axios.get('add/languages')
				.then(({
					data
				}) => {
					this.languages = data;
				})
				.catch(() => {
					displayError(this.$gettext('Something has gone wrong during get language request'));
				});
		},

		/**
		 * Resets the user form so it can be done over.
		 *
		 * @returns {void}
		 */
		resetUserForm () {
			this.resetUserData();
			this.client.email = null;
		},

		/**
		 * Reset user data
		 *
		 * @returns {void}
		 */
		resetUserData () {
			this.client.name.initials = null;
			this.client.name.first = null;
			this.client.name.infix = null;
			this.client.name.last = null;
			this.client.dateBirth = null;
			this.client.gender = null;
			this.client.language = null;
			this.client.isBusiness = false;
			this.client.business.name = null;
			this.client.business.kvk = null;
			this.client.address = {
				'street': '',
				'housenumber': '',
				'housenumberSuffix': '',
				'postalcode': '',
				'city': '',
				'doorCode': '',
				'administrativeArea': '',
				'houseName': '',
				'residentialArea': '',
				'floor': '',
				'country': this.domain.country.name,
			};
			this.client.telephonenumber = null;
			this.client.bankaccount.iban = null;
			this.knownUser = null;
			this.hasActiveInsurance = false;
		},

		/**
		 * Find user and set all the data of it if found
		 *
		 * @returns {void}
		 */
		findUser () {
			if(this.$refs.email.validate()) {
				this.step = 1;
				axios.get('add/user', {
					params: {
						email: this.client.email
					}
				})
					.then(({
						data
					}) => {
						if (data) {
							this.client.name.initials = data.name_initials;
							this.client.name.first = data.name_first;
							this.client.name.infix = data.name_infix;
							this.client.name.last = data.name_last;
							this.client.dateBirth = data.date_birth;
							this.client.gender = data.gender;
							this.client.language = data.language;
							this.client.isBusiness = !!data.is_business;
							this.client.business.name = data.business_name;
							this.client.business.kvk = data.business_kvk;
							this.client.address.street = data.address_street;
							this.client.address.housenumber = data.address_number;
							this.client.address.housenumberSuffix = data.address_number_suffix;
							this.client.address.postalcode = data.address_postalcode;
							this.client.address.city = data.address_city;
							this.client.address.doorCode = data.door_code;
							this.client.address.administrativeArea = data.address_administrative_area;
							this.client.address.houseName = data.address_house_name;
							this.client.address.residentialArea = data.address_residential_area;
							this.client.address.floor = data.address_floor;
							this.client.address.country = data.country;
							this.client.telephonenumber = data.telephonenumber;
							this.client.bankaccount.iban = data.bankaccount_iban;
							this.hasActiveInsurance = data.active_insurance;
						} else {
							this.hasActiveInsurance = false;
						}
						this.knownUser = !!data;
					})
					.catch(() => {
						this.knownUser = null;
						displayError(this.$gettext('Something has gone wrong during insurance fetch'));
					});
			}
		},

		/**
		 * If client form is valid, we proceed
		 *
		 * @returns {void}
		 */
		async validateUser () {
			const {
				valid
			} = await this.$refs.clientForm.validate();

			if(valid) {
				this.$emit('update:client', this.client);
				if (this.clientDocument.length > 0) {
					this.$emit('update:document', this.clientDocument[0]);
				}
			}
		},
	}
};
</script>
